/* rodacCatalog.js — cliente RODAC para la calculadora del hero (Fase 2).
 *
 * Los payloads son ESPEJO 1:1 de Views/Cotizador/functions/rodac.js (getModels,
 * getBrands, getTypes, getDescriptions, fetchInsurancesSingle). No se reusan
 * esas funciones directamente porque están acopladas a (state, setState) con el
 * shape de useCarCalculator; aquí necesitamos además la UNIÓN de catálogos
 * AUTO+PICKUP para respetar el orden Marca→Modelo del diseño del hero
 * (RODAC separa por IDTipoVehiculo y el hero no pregunta tipo primero).
 *
 * El backend (POST /cotizador, /cotizador2) se reusa tal cual — ver useHeroQuote.
 */
import axios from 'axios';
import { rodac_api, api } from '../../../env';
import { getAddressForStatePlate } from '../../../Utils/statePlateAddress';

const FORM_HEADERS = { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } };

/* ── Constantes (mismos valores hardcodeados que rodac.js / useCarCalculator) ── */
export const SUBCHANNEL_ID = 20;            // rodac.js:42
export const CAR_TYPE_AUTO = 1;             // rodac.js getCarTypes
export const CAR_TYPE_PICKUP = 2;
export const CAR_STATUS_NUEVO = 1;          // manual cotiza NUEVO; la IA detecta seminuevo
export const CAR_STATUS_SEMINUEVO = 2;
export const INSURANCE_DEFAULTS = {
  use_type_id: 1,                           // PARTICULAR (rodac.js:185)
  deductible_id: 1,                         // DM 5% | RT 10% (rodac.js:235)
  package_id: 2,                            // AMPLIA (rodac.js:331)
  udi_id: null,                             // fetch_udis deshabilitado ("trae error")
  compensation_id: 1,                       // Valor Factura (NUEVO) — useCarCalculator:782
};
/* Dirección default para cotizar seguros sin pedir CP en el hero —
 * mismo fallback CDMX que useCarCalculator (STATE_PLATE_ADDRESS_FALLBACKS). */
export const DEFAULT_ADDRESS = {
  license_state: 1, // CDMX (StatePlate id 1)
  zip: '03710',
  colonia: 'CIUDAD DE LOS DEPORTES',
  municipio: 'BENITO JUAREZ',
  ciudad: 'CIUDAD DE MEXICO',
  estado: 'CIUDAD DE MEXICO',
};

/* Dirección con la que se cotizan seguro y renta según la placa elegida
 * (P3 · D-ADM-7): la default de la placa en BD → respaldo por nombre → CDMX.
 * placas = `state` de GET api/getStaticData; placaId = StatePlate.id. */
export function addressForPlaca(placas, placaId) {
  const placa = (placas || []).find(p => +p.id === +placaId);
  return (placa && getAddressForStatePlate(placa)) || DEFAULT_ADDRESS;
}

/* Llave del cache de seguros: RODAC cotiza por auto + suma asegurada + CP, así
 * que la placa y su CP entran (D-ADM-7/17). El plazo no: solo re-selecciona. */
export const insuranceCacheKey = ({ descriptionId, carStatusId, precio, placa, zip }) =>
  `${descriptionId}|${carStatusId}|${precio}|${placa}|${zip}`;

/* Plazos — DEFAULT_PAYMENT_CONFIGURATIONS de useCarCalculator.js:31 */
export const PAYMENT_CONFIGS = [
  { id: 13, configuration: '24' },
  { id: 14, configuration: '36' },
  { id: 15, configuration: '48' },
  { id: 16, configuration: '60' },
];
export const configIdForPlazo = (plazo) =>
  PAYMENT_CONFIGS.find(c => +c.configuration === +plazo)?.id ?? null;

const delay = (ms = 200) => new Promise(r => setTimeout(r, ms));

/* ── Diccionario en backend (caché 24h) ─────────────────────────────────
 * GET api/v1/catalog/* sirve el catálogo cacheado (warm-up diario 6am) en
 * UNA llamada en vez de 2-5 directas a RODAC. Si el backend falla (503,
 * caído, etc.) cada fetcher cae al modo directo contra RODAC de abajo. */
async function fromDictionary(path, params) {
  const res = await axios.get(`${api}/v1/catalog/${path}`, { params, timeout: 15000 });
  return res.data;
}

/* Variantes Nueva/Usada disponibles de una lista de modelos (sección
 * "¿O prefieres…"). El backend valida cada modelo contra el diccionario, así
 * nunca ofrecemos un seminuevo que no exista en RODAC. Devuelve [] si falla. */
export async function fetchVariants(models, maxBack = 2) {
  try {
    const res = await axios.post(`${api}/v1/catalog/variants`, { models, maxBack }, { timeout: 30000 });
    return Array.isArray(res.data?.variants) ? res.data.variants : [];
  } catch (e) {
    return [];
  }
}

/* ── Años (RODAC "modelos") por condición — espejo de getModels (rodac.js:422) ── */
async function fetchYearsDirect(statusId) {
  const res = await axios.post(`${rodac_api}/Autos/ConsultaModelos`, {
    IdSubcanal: SUBCHANNEL_ID,
    IDEstatusVehiculo: statusId,
  }, FORM_HEADERS);
  if (res.data.Data.HasError) throw new Error('RODAC ConsultaModelos');
  return res.data.Data.Data.map(item => ({ id: item.Id, year: String(item.Valor) }));
}
export async function fetchYears(statusId = CAR_STATUS_NUEVO) {
  try {
    const { years } = await fromDictionary('years', { status: statusId });
    if (Array.isArray(years) && years.length) return years;
    throw new Error('vacío');
  } catch (e) {
    return fetchYearsDirect(statusId);
  }
}

/* ── Marcas (unión AUTO + PICKUP) — espejo de getBrands (rodac.js:464) ── */
async function fetchBrandsFor(carTypeId, yearId) {
  const res = await axios.post(`${rodac_api}/Autos/ConsultaMarcas`, {
    IDTipoVehiculo: carTypeId,
    modelo: yearId,
  }, FORM_HEADERS);
  if (res.data.Data.HasError) return [];
  return res.data.Data.Data.map(item => ({ brand: item.Valor, carTypeId }));
}
async function fetchBrandsUnionDirect(yearId) {
  const [autos, pickups] = await Promise.all([
    fetchBrandsFor(CAR_TYPE_AUTO, yearId),
    fetchBrandsFor(CAR_TYPE_PICKUP, yearId),
  ]);
  // dedupe por nombre, recordando en qué tipo(s) existe la marca
  const map = new Map();
  [...autos, ...pickups].forEach(({ brand, carTypeId }) => {
    const prev = map.get(brand) || { brand, carTypeIds: [] };
    if (!prev.carTypeIds.includes(carTypeId)) prev.carTypeIds.push(carTypeId);
    map.set(brand, prev);
  });
  return [...map.values()].sort((a, b) => a.brand.localeCompare(b.brand));
}
export async function fetchBrandsUnion(yearId) {
  try {
    const { brands } = await fromDictionary('brands', { year: yearId });
    if (Array.isArray(brands) && brands.length) return brands;
    throw new Error('vacío');
  } catch (e) {
    return fetchBrandsUnionDirect(yearId);
  }
}

/* ── Modelos (RODAC "tipos", unión por tipo de vehículo) — espejo de getTypes (rodac.js:506) ── */
async function fetchTypesFor(carTypeId, yearId, brand) {
  const res = await axios.post(`${rodac_api}/Autos/ConsultaTipos`, {
    IDTipoVehiculo: carTypeId,
    modelo: yearId,
    submarca: brand,
  }, FORM_HEADERS);
  if (res.data.Data.HasError) return [];
  return res.data.Data.Data.map(item => ({ type: item.Valor, carTypeId }));
}
async function fetchModelTypesUnionDirect(yearId, brand, carTypeIds) {
  const lists = await Promise.all(carTypeIds.map(ct => fetchTypesFor(ct, yearId, brand)));
  const map = new Map();
  lists.flat().forEach(({ type, carTypeId }) => {
    if (!map.has(type)) map.set(type, { type, carTypeId }); // AUTO gana si está en ambos
  });
  return [...map.values()].sort((a, b) => a.type.localeCompare(b.type));
}
export async function fetchModelTypesUnion(yearId, brand, carTypeIds) {
  try {
    const { models } = await fromDictionary('models', { year: yearId, brand, carTypes: carTypeIds.join(',') });
    if (Array.isArray(models) && models.length) return models;
    throw new Error('vacío');
  } catch (e) {
    return fetchModelTypesUnionDirect(yearId, brand, carTypeIds);
  }
}

/* ── Versiones (RODAC "descripciones") — espejo de getDescriptions (rodac.js:549) ── */
async function fetchVersionsDirect(carTypeId, yearId, brand, type) {
  const res = await axios.post(`${rodac_api}/Autos/ConsultaDescripciones`, {
    IDTipoVehiculo: carTypeId,
    modelo: yearId,
    submarca: brand,
    tipo: type,
  }, FORM_HEADERS);
  if (res.data.Data.HasError) throw new Error('RODAC ConsultaDescripciones');
  return res.data.Data.Data.map(item => ({ id: item.Id, description: item.Valor }));
}
export async function fetchVersions(carTypeId, yearId, brand, type) {
  try {
    const { versions } = await fromDictionary('versions', { year: yearId, brand, type, carType: carTypeId });
    if (Array.isArray(versions) && versions.length) return versions;
    throw new Error('vacío');
  } catch (e) {
    return fetchVersionsDirect(carTypeId, yearId, brand, type);
  }
}

/* ── Seguros por plazo — espejo de fetchInsurancesSingle (rodac.js:791) ──
 * Devuelve un array por cada payment config: [{id, amount, logo}]
 * address: {zip, colonia, municipio, ciudad, estado} de la placa (default CDMX). */
async function fetchInsurancesForConfig(configId, stagger, { descriptionId, carTypeId, amount, carStatusId = CAR_STATUS_NUEVO, address }) {
  const addr = address || DEFAULT_ADDRESS;
  await delay(stagger);
  const res = await axios.post(`${rodac_api}/Autos/Cotizaciones`, {
    ClaveVehiculo: descriptionId,
    IDTipoVehiculo: carTypeId,
    IDEstatusVehiculo: carStatusId,
    // Nuevo=1 Valor Factura, Seminuevo=4 Valor Comercial (useCarCalculator:782)
    IDIndenmizacion: carStatusId === CAR_STATUS_SEMINUEVO ? 4 : INSURANCE_DEFAULTS.compensation_id,
    SumaAsegurada: amount,
    CodigoPostal: addr.zip,
    IDTipoUso: INSURANCE_DEFAULTS.use_type_id,
    IDDeducibles: INSURANCE_DEFAULTS.deductible_id,
    IDPagoVigencia: configId,
    IDUDI: INSURANCE_DEFAULTS.udi_id,
    IDPaquete: INSURANCE_DEFAULTS.package_id,
    ColoniaPersona: addr.colonia,
    MunicipioPersona: addr.municipio,
    CiudadPersona: addr.ciudad,
    EstadoPersona: addr.estado,
    IDSubcananal: SUBCHANNEL_ID, // sic — así se llama el campo en rodac.js
  }, FORM_HEADERS);
  const data = res.data?.Data?.Data;
  if (!Array.isArray(data)) return [];
  return data
    .filter(item => !item.HasError)
    .map(item => ({
      id: item.IDCotizacion,
      amount: +item.PrimaTotal,
      logo: item.LogoAseguradora,
      serial: item.Folio,
    }));
}

/* Cotiza seguros para los 4 plazos (escalonado 400ms como getThreeInsurances).
 * address = dirección de la placa (addressForPlaca); sin ella, CDMX.
 * Devuelve { [plazo]: [{id, amount, logo}] } */
export async function fetchInsuranceQuotes({ descriptionId, carTypeId, amount, carStatusId = CAR_STATUS_NUEVO, address = DEFAULT_ADDRESS }) {
  const results = await Promise.all(
    PAYMENT_CONFIGS.map((cfg, i) =>
      fetchInsurancesForConfig(cfg.id, i * 400, { descriptionId, carTypeId, amount, carStatusId, address })
    )
  );
  const byPlazo = {};
  PAYMENT_CONFIGS.forEach((cfg, i) => { byPlazo[+cfg.configuration] = results[i]; });
  return byPlazo;
}

/* ¿Algún plazo trajo al menos una aseguradora válida? Hay autos donde RODAC
 * responde 200 pero TODAS las aseguradoras vienen con HasError:true (versión
 * sin mapear en GNP, sin clave vehicular en HDI, 502 upstream de Chubb…) →
 * todos los arrays quedan vacíos. useHeroQuote usa esto para el retry
 * automático y el fallback a WhatsApp. */
export function hasAnyInsurance(quotesByPlazo) {
  return Object.values(quotesByPlazo || {}).some(list => list && list.length > 0);
}

/* Selección de aseguradora: por default la más barata en el plazo elegido —
 * o la preferida por el usuario (preferredLogo, paso Seguro del modal F3) si
 * cotizó ese plazo. Para los demás plazos, la misma compañía (o la más barata
 * si no existe) — mismo criterio que useCarCalculator.js:896-935.
 * Devuelve { selected, cheapest, byPlazo: {plazo: opción} } */
export function pickInsurances(quotesByPlazo, plazo, preferredLogo = null) {
  const current = quotesByPlazo[plazo] || [];
  if (!current.length) return null;
  const sorted = [...current].sort((a, b) => a.amount - b.amount);
  const cheapest = sorted[0];
  const selected = (preferredLogo && current.find(o => o.logo === preferredLogo)) || cheapest;
  const byPlazo = {};
  Object.entries(quotesByPlazo).forEach(([p, options]) => {
    const same = options.find(o => o.logo === selected.logo);
    byPlazo[p] = same || [...options].sort((a, b) => a.amount - b.amount)[0] || null;
  });
  return { selected, cheapest, byPlazo };
}
